import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { acquireGpsFix, type GpsAcquireError } from '../utils/gpsPlacement';

export type GeolocationErrorType = 'denied' | 'timeout' | 'unavailable' | null;

/**
 * Result of a `requestLocation` invocation. Always resolved (never thrown):
 * - On success: `coords` AND the browser-reported `accuracyMeters` (T011: a
 *   fix without accuracy is not a fix — the containment endpoint needs it).
 * - On failure: `coords`/`accuracyMeters` undefined, `errorType` set.
 */
export type RequestResult = {
  coords?: [number, number];
  accuracyMeters?: number;
  errorType?: GeolocationErrorType;
};

export interface GeolocationContextType {
  location: [number, number] | undefined;
  loading: boolean;
  error: string | null;
  errorType: GeolocationErrorType;
  /**
   * Lazily request the user's current position. Implements a fast-then-accurate
   * fallback strategy. Always resolves (never throws).
   *
   * @param options.silent When true, the in-flight `loading` flag is NOT toggled
   *                      (useful for background refreshes that shouldn't flash UI).
   */
  requestLocation: (options?: { silent?: boolean }) => Promise<RequestResult>;
}

const GeolocationContext = createContext<GeolocationContextType | undefined>(undefined);

const ERROR_MESSAGES: Record<GpsAcquireError, string> = {
  denied: 'User denied geolocation permission',
  unavailable: 'Position information unavailable',
  timeout: 'Geolocation request timed out',
};

interface GeolocationProviderProps {
  children: ReactNode;
}

/**
 * Geolocation provider component.
 *
 * Lazy/on-demand: does NOT request the user's position at mount. Consumers must
 * call `requestLocation()` explicitly (e.g., from a "Use my location" button).
 */
export function GeolocationProvider({ children }: GeolocationProviderProps) {
  const [location, setLocation] = useState<[number, number] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<GeolocationErrorType>(null);

  // Tracks the most recent in-flight request. Stale callbacks (whose id no longer
  // matches the current ref) become no-ops, mirroring the original useEffect's
  // `cancelled` cleanup pattern.
  const activeRequestIdRef = useRef(0);

  const requestLocation = useCallback(
    async (options?: { silent?: boolean }): Promise<RequestResult> => {
      const silent = options?.silent === true;
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      const isStale = () => activeRequestIdRef.current !== requestId;

      if (!silent) {
        setLoading(true);
      }

      const finish = (result: RequestResult): RequestResult => {
        if (!isStale() && !silent) setLoading(false);
        return result;
      };

      if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
        if (!isStale()) {
          setError('Geolocation is not supported by this browser');
          setErrorType('unavailable');
        }
        return finish({ errorType: 'unavailable' });
      }

      // One acquisition path (T011): the shared helper always reports accuracy.
      const applySuccess = (fix: { lat: number; lng: number; accuracyMeters: number }) => {
        if (!isStale()) {
          setLocation([fix.lat, fix.lng]);
          setError(null);
          setErrorType(null);
        }
        return finish({
          coords: [fix.lat, fix.lng],
          accuracyMeters: fix.accuracyMeters,
        });
      };

      const fast = await acquireGpsFix('fast');
      if (fast.kind === 'ok') return applySuccess(fast.fix);

      // HEAD staleness semantics (W2, T011 review): a SUPERSEDED request
      // resolves with `errorType: undefined` — its outcome is irrelevant to the
      // caller — and never triggers the accurate retry (no extra acquisition
      // HEAD would not have made).
      if (isStale()) return finish({ errorType: undefined });

      // Permission denied is terminal — no point retrying with high accuracy.
      if (fast.errorType === 'denied') {
        if (!isStale()) {
          setError(ERROR_MESSAGES.denied);
          setErrorType('denied');
        }
        return finish({ errorType: 'denied' });
      }

      // Timeout or position unavailable: retry with high accuracy.
      const accurate = await acquireGpsFix('high');
      if (accurate.kind === 'ok') return applySuccess(accurate.fix);
      if (isStale()) return finish({ errorType: undefined });
      setError(ERROR_MESSAGES[accurate.errorType]);
      setErrorType(accurate.errorType);
      return finish({ errorType: accurate.errorType });
    },
    []
  );

  const value = useMemo<GeolocationContextType>(
    () => ({
      location,
      loading,
      error,
      errorType,
      requestLocation,
    }),
    [location, loading, error, errorType, requestLocation]
  );

  return <GeolocationContext.Provider value={value}>{children}</GeolocationContext.Provider>;
}

/**
 * Hook to access geolocation context.
 * @throws Error if used outside of GeolocationProvider
 */
export function useGeolocationContext(): GeolocationContextType {
  const context = useContext(GeolocationContext);
  if (context === undefined) {
    throw new Error('useGeolocationContext must be used within a GeolocationProvider');
  }
  return context;
}
